/**
 * PublicHeader — the consistent public (signed-out) site header.
 *
 * Replaces the old minimal HamburgerMenu on public marketing pages
 * (contest brief, 2026-07-11). Layout:
 *
 *   [Logo lockup]      Demo · Pricing · About · For professionals      [Sign in]
 *
 * Below `md`, the inline nav collapses into a hamburger sheet holding the
 * full public site map. Signed-in visitors see "Go to app" instead of
 * "Sign in" (same behavior the old HamburgerMenu had).
 *
 * Mounted by AppRouter (App.js) — pages never render their own header.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/demo", label: "Demo", match: (p) => p.startsWith("/demo") },
  { to: "/pricing", label: "Pricing", match: (p) => p === "/pricing" },
  { to: "/about-us", label: "About", match: (p) => p === "/about-us" || p === "/about" },
  {
    to: "/therapist-portal",
    label: "For professionals",
    match: (p) => p === "/therapist-portal" || p === "/schools-portal",
  },
];

// Extra destinations that only appear inside the mobile sheet.
const SHEET_EXTRAS = [
  { to: "/ready-boards", label: "Ready Boards" },
  { to: "/schools-portal", label: "For schools" },
  { to: "/privacy-promise", label: "Our privacy promise" },
  { to: "/contact", label: "Contact us" },
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isAuthenticated = !!user && user !== false;

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const onHome = location.pathname === "/";
  const cta = isAuthenticated
    ? { to: "/pick-board", label: "Go to app", testid: "header-goto-app" }
    : { to: "/register", label: "Sign up", testid: "header-signup" };

  return (
    <header
      className="sticky top-0 z-40 bg-[color:var(--cream)] border-b-[3px] border-[color:var(--ink)]"
      data-testid="public-header"
    >
      {/* One slim bar, all in the hamburger menu bar:
          hamburger + "Made by Emergent" (left) · SonSpeak logo (center) ·
          Sign up (right). Buttons hug their text. */}
      <div className="relative max-w-6xl mx-auto px-3 sm:px-5 h-12 flex items-center gap-2">
        {/* Hamburger — left */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md text-[color:var(--ink)] hover:bg-[color:var(--ink)]/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--gold)] shrink-0"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="public-header-sheet"
          data-testid="hamburger-toggle"
        >
          {open ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
        </button>

        {/* Center of the bar: on home it's the "Made by Emergent" credit
            (the SonSpeak logo is the hero, above "AAC for families"); on every
            other page it's the SonSpeak logo linking home. */}
        {onHome ? (
          <a
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center bg-[color:var(--ink)] text-white text-[11px] font-bold uppercase tracking-[0.12em] leading-none px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
            data-testid="header-made-by-emergent"
          >
            Made by Emergent
          </a>
        ) : (
          <Link
            to="/"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--gold)] rounded-md"
            aria-label="SonSpeak — home"
            data-testid="header-logo"
          >
            <img
              src="/brand/sonspeak-logo-transparent.png"
              alt="SonSpeak"
              className="h-8 sm:h-9 w-auto"
            />
          </Link>
        )}

        <div className="flex-1" />

        {/* Sign up / Go to app — right, hugs its text */}
        <Link
          to={cta.to}
          className="pill-btn primary text-xs px-3 py-1.5 shrink-0"
          data-testid={cta.testid}
        >
          {cta.label}
        </Link>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          id="public-header-sheet"
          className="absolute left-0 right-0 top-full bg-[color:var(--paper)] border-b-[3px] border-[color:var(--ink)] shadow-xl"
          data-testid="hamburger-sheet"
        >
          <nav aria-label="Mobile" className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
            <ul className="divide-y divide-[color:var(--ink)]/10">
              {[...NAV, ...SHEET_EXTRAS].map((item) => (
                <li key={item.to + item.label}>
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between py-3 text-base font-semibold text-[color:var(--ink)] hover:text-[color:var(--coral-deep)] transition-colors"
                    data-testid={`hamburger-item-${item.to.replace(/^\//, "").replace(/[^a-z0-9-]+/gi, "-")}`}
                  >
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={cta.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-3 text-base font-semibold text-[color:var(--coral-deep)]"
                  data-testid="hamburger-item-signup"
                >
                  <span>{cta.label}</span>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
}
